<template>
  <div
    id="project-docs-attachments"
    class="edit-attachments"
  >
    <p v-show="!files.length && showEmptyMsg">
      {{ emptyMsg }}
    </p>
    <table
      v-show="files.length>0"
      class="tender-tnc-table"
    >
      <thead>
        <th class="number edit-attachments__header">
          #
        </th>
        <th
          class="filename edit-attachments__header"
          :class="{'pointer': sortingToggle}"
          @click="sortBy('filename')"
        >
          {{ $t('table.header.document') }}
          <HKLSortingIcon
            v-if="shouldShowSortIcon('filename')"
            :iconDirection="sortIconDirection"
          />
        </th>
        <th
          v-if="isShowDes"
          class="description edit-attachments__header"
          :class="{'pointer': sortingToggle}"
          @click="sortBy('description')"
        >
          {{ $t('table.header.description') }}
          <HKLSortingIcon
            v-if="shouldShowSortIcon('description')"
            :iconDirection="sortIconDirection"
          />
        </th>
        <template v-if="attachmentTypes.length">
          <th
            v-for="type in attachmentTypes"
            :key="type.typeName"
            class="type edit-attachments__header"
            :class="kebabCase(type.attrName)"
          >
            {{ type.typeName }}
          </th>
        </template>
        <th
          v-if="isShowUploadedDate"
          :class="{'pointer': sortingToggle}"
          class="uploaded-date edit-attachments__header"
          @click="sortBy('createdAt')"
        >
          {{ $t('table.header.uploadedDate') }}
          <HKLSortingIcon
            v-if="shouldShowSortIcon('createdAt')"
            :iconDirection="sortIconDirection"
          />
        </th>
        <th v-if="canDelete" />
      </thead>
      <draggable
        :options="{handle:'.myHandler'}"
        element="tbody"
        :list="files"
        @end="updateAllOrder()"
      >
        <template v-for="(item, index) in sortedFiles">
          <tr
            :key="index"
            class="set-border-top"
            :class="{myHandler: canEdit}"
          >
            <td class="number tender-font">
              <span class="attachment-index">{{ index + 1 }}</span>
            </td>
            <td class="filename tender-font">
              <a
                style="text-decoration: underline;"
                @click="downloadFile(item.id)"
              >
                {{ item.filename }}
              </a>
            </td>
            <td
              v-if="isShowDes"
              class="description tender-font"
            >
              <div v-show="allowChangeName[item.id]">
                <textarea
                  v-model="item.description"
                  v-autosize
                  v-checkVisibility="{refresh: true}"
                  class="autosize file-upload-desc"
                  autofocus
                  @focusout="updateDescription(item)"
                />
              </div>
              <div v-show="!allowChangeName[item.id]">
                <div
                  class="emphasis description-text"
                  @dblclick="dblClickEdit(item)"
                >
                  {{ item.description || (canEdit || showEditPencil(item, currentUser) ? $t('project.docs.fillDescHint') : '') }}<!--
                    Comment for removing new line inside element
                  -->
                  <a
                    v-if="canEdit || showEditPencil(item, currentUser)"
                    id="settings-engineers-security-group-detail-name-edit"
                    class="fa fa-pencil icon"
                    @click="Vue.set(allowChangeName, item.id, true)"
                  />
                </div>
              </div>
            </td>
            <template v-if="attachmentTypes.length">
              <td
                v-for="(type) in attachmentTypes"
                :key="type.typeName"
              >
                <input
                  :key="`${type.typeName}-${index}`"
                  type="checkbox"
                  :disabled="!canEdit"
                  :checked="!!item.metaData[type.attrName]"
                  @change="(e) => updateType(e, type, item)"
                >
              </td>
            </template>
            <td
              v-if="isShowUploadedDate"
              class="uploaded-at"
              style="text-align: right;"
            >
              {{ formatDate(item.createdAt) }}
            </td>
            <td v-if="canDelete">
              <div
                v-if="showDeleteBtn(item, index)"
                class="file-upload-delete"
                @click="deleteFile(item, index)"
              >
                <i class="fa fa-times fa-lg" />
              </div>
            </td>
          </tr>
        </template>
      </draggable>
    </table>
    <div
      v-show="canDownloadAll && files.length > 0"
      class="projects-download-all clearfix"
    >
      <button
        class="btn btn--darkblue tender-sticky-button float-left"
        type="button"
        @click="downloadAll(sourceId)"
      >
        {{ downloadString }}
      </button>
      <div class="float-right clearfix">
        <slot name="buttons" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Vue from 'vue'
import { unref, ref, getCurrentInstance, computed } from 'vue-demi'
import * as xfs from '@/utils/xfs'
import dateUtil from '@/utils/dateUtil'
import { getAttachments, updateDescription, updateOrder, deleteDocument, deleteAllDocuments } from '@/api/docs/request'
import EventBus from '@/utils/eventBus'
import { useStoreGetters } from '@/store'
import draggable from 'vuedraggable'
import querystring from 'querystring'
import lodash from 'lodash'
import { SORT_DIRECTION } from '@/utils/sortingHelper'
import HKLSortingIcon from './Icon/HKLSortingIcon.vue'
import i18n from '@/utils/i18n'

/**
 * Edit attachments for HKLand
 * @type {import('vue').Component}
 * @props {boolean} [uploadOnTop = false] Need combine with `uploadOnTop` in HkldDropzone component. Because the attachments can be sorted via dragging to change order attribute.
 * @example
 * ```jsx
 * <EditAttachments :uploadOnTop="true" />
 * ```
 */
export default {
  name: 'EditAttachments',
  components: {
    HKLSortingIcon,
    draggable,
  },

  props: {
    canEdit: {
      type: Boolean,
      default () { return false },
    },
    canDelete: {
      type: Boolean,
      default () { return false },
    },
    showAllDeleteBtn: {
      type: Boolean,
      default: true,
    },
    showEditPencil: {
      type: Function,
      default: () => false,
    },
    showDeleteBtnFunc: {
      type: Function,
      default: undefined,
    },
    canDeleteItem: {
      type: Function,
      default: () => false,
    },
    canDownloadAll: {
      type: Boolean,
      default () { return false },
    },
    sourceId: {
      type: String,
      required: true,
    },
    zipName: {
      type: String,
      default: '',
    },
    action: {
      type: String,
      default: '',
    },
    isShowDes: {
      type: Boolean,
      default: true,
    },
    isShowUploadedDate: {
      type: Boolean,
      default: true,
    },
    getFileLengthID: {
      type: [String, Number],
      default: '',
    },
    filesChanged: {
      type: Function,
      default: () => {},
    },
    getFilesList: {
      type: Function,
      default: () => {},
    },
    hiddenAttachments: {
      type: Array,
      default () {
        return []
      },
    },
    initLoading: {
      type: Boolean,
      default: true,
    },
    updateDescriptionCb: {
      type: Function,
      default: () => {},
    },
    deleteFileCb: {
      type: Function,
      default: () => {},
    },
    uploadSuccessCb: {
      type: Function,
      default: () => {},
    },
    showEmptyMsg: {
      type: Boolean,
      default: false,
    },
    emptyMsg: {
      type: String,
      default: '',
    },
    attachmentTypes: {
      type: Array,
      default: () => [],
    },
    filesFilterFn: {
      type: Function,
      default: (items) => items,
    },
    uploadOnTop: {
      type: Boolean,
      default: false,
    },
    sortingToggle: {
      type: Boolean,
      default: false,
    },
    downloadString: {
      type: String,
      default: i18n.t('action.downloadAll'),
    },
  },
  setup (props) {
    const { proxy } = getCurrentInstance()
    const { currentUser } = useStoreGetters('user', ['currentUser'])
    const sortOrderMap = {
      [SORT_DIRECTION.ASC]: true,
      [SORT_DIRECTION.DESC]: false,
    }
    const sortOrder = ref(sortOrderMap[SORT_DIRECTION.ASC])
    const sortingAttr = ref(null)
    return {
      sortOrder,
      sortingAttr,
      sortBy: (attrName) => {
        if (!props.sortingToggle) return
        sortingAttr.value = attrName
        sortOrder.value = !sortOrder.value
      },
      shouldShowSortIcon: (attrName) => {
        if (!props.sortingToggle) return false
        return attrName === sortingAttr.value
      },
      sortedFiles: computed(() => {
        if (!sortingAttr.value || !props.sortingToggle) return proxy.files
        const result = lodash.orderBy(proxy.files, [sortingAttr.value])
        return sortOrder.value ? result : result.reverse()
      }),
      sortIconDirection: computed(() => {
        return lodash.findKey(sortOrderMap, (v) => v === sortOrder.value)
      }),
      showDeleteBtn: (item, index) => {
        if (typeof props.showDeleteBtnFunc === 'function') return props.showDeleteBtnFunc(item, index)
        if (props.showAllDeleteBtn) return props.showAllDeleteBtn
        return unref(currentUser).id === item.createdBy
      },
    }
  },
  data () {
    return {
      Vue,
      dateUtil,
      prevSourceId: '',
      allowChangeName: {},
      files: [],
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser']),
  },

  watch: {
    async sourceId () {
      this.setupListener()
      if (this.initLoading) {
        await this.loadFiles()
      }
    },
    async hiddenAttachments () {
      if (this.initLoading) {
        await this.loadFiles()
      }
    },
  },

  async mounted () {
    this.setupListener()
    if (this.initLoading) {
      await this.loadFiles()
    }
  },

  destroyed () {
    if (this.sourceId) {
      EventBus.$off(`controls.hkldDropzone.newItem.${this.sourceId}`)
    }
  },
  methods: {
    kebabCase: lodash.kebabCase,
    async deleteFile (item) {
      const headers = {}
      if (this.action) {
        headers['X-CMS-Action'] = this.action
      }
      const deleted = await deleteDocument(item.id, headers)
      if (deleted) {
        this.files.splice(this.files.indexOf(item), 1)
        this.filesChanged(this.files, item.id)
        this.deleteFileCb(item, this.files)
        EventBus.$emit(`controls.hkldDropzone.deleteItem.${this.sourceId}`, item)
      }
      this.getFilesList(this.files)
    },

    async downloadFile (fileId) {
      await xfs.downloadFile(`/docs/${fileId}`)
    },

    async downloadAll (sourceId) {
      const filesId = this.files.map(file => file.id)
      const query = querystring.stringify({docsId: filesId})
      await xfs.downloadFile(`/docs/all/${sourceId}?${query}`, this.zipName)
    },

    async updateDescription (item) {
      await updateDescription(item)
      this.updateDescriptionCb(item)
      Vue.set(this.allowChangeName, item.id, false)
    },
    async updateType (e, type, item) {
      item.metaData[type.attrName] = e.target.checked
      await updateDescription(item, { metaData: item.metaData })
      this.filesChanged(this.files, item.id)
      this.getFilesList(this.files)
    },

    async updateAllOrder () {
      await Promise.all(
        this.files.map(async (file, index) => {
          file.order = index
          await updateOrder(file.id, index)
        }),
      )
    },

    formatDate (dateString) {
      return dateUtil.formatDate(dateString, true, true)
    },

    async loadFiles () {
      if (!this.sourceId) return
      let result = await getAttachments(this.sourceId)
      if (this.hiddenAttachments.length > 0) {
        const hiddenIds = this.hiddenAttachments.map(item => item.id)
        result = this.files.filter((item) => hiddenIds.indexOf(item.id) === -1)
      }
      if (typeof this.filesFilterFn === 'function') {
        const ref = this.filesFilterFn(result)
        result = Array.isArray(ref) ? ref : []
      }
      this.files = result || []
      this.$parent.$emit(`controls.hkldDropzone.loadFilesLength${this.getFileLengthID}`, this.files.length)
      this.getFilesList(this.files)
    },

    addNewItem (newItem) {
      const isExist = this.files.some(item => item.id === newItem.id)
      if (!isExist) {
        this.uploadOnTop ? this.files.unshift(newItem) : this.files.push(newItem)
        this.uploadSuccessCb(newItem)
      }
    },

    async cleanAllFiles (backend = true) {
      if (!(this.sourceId && this.files.length)) return
      if (backend) {
        await deleteAllDocuments(this.sourceId)
      }
      this.files = []
      this.getFilesList(this.files)
    },

    setupListener () {
      if (this.sourceId) {
        EventBus.$off(`controls.hkldDropzone.newItem.${this.prevSourceId}`)
        EventBus.$on(`controls.hkldDropzone.newItem.${this.sourceId}`, (newItem) => {
          this.addNewItem(newItem)
          this.filesChanged(this.files, newItem.id)
          this.getFilesList(this.files)
        })
        this.prevSourceId = this.sourceId
      }
    },

    dblClickEdit (item) {
      if (this.canEdit || this.showEditPencil(item, this.currentUser)) {
        Vue.set(this.allowChangeName, item.id, true)
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '~styles/tender/attachments';

pre {
  display: inline-block;
  vertical-align: top;
}

.filename {
  width: 25%;
}

.description {
  width: 50%;
  .description-text {
    white-space: normal;
    .fa{
      margin-left: 5px;
    }
  }
}

.emphasis {
  font-weight: inherit;
}

.uploaded-date {
  width: 20%;
  padding: .9rem;
  text-align: right;
  text-transform: uppercase;
  white-space: nowrap;
}

.myHandler:hover {
  cursor: move;
}

.effective-date .vdp-datepicker {
    border: none;
    border-right: none;
    background-color: transparent;
    padding: 0 0;
}

.file-upload-delete {
  cursor: pointer;
}
.filename {
  word-break: break-all
}
tbody td:has(input[type="checkbox"]) {
  text-align: center;
}
</style>
